import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  Button,
  Input,
  List,
  Pagination,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useMemo, useState } from 'react'
import { useDebouncedState } from '../../../../hooks/useDebouncedState'
import VSpace from '../../../common/VSpace'
import {
  downloadMockJson,
  MCP_MOCKS_PAGE_SIZE,
  McpMock,
} from './mcpMocksAdminModel'
import {
  useDeleteMcpMockMutation,
  useMcpMocksListQuery,
} from './mcpMocksAdminQueries'
import McpMockUploadModal from './McpMockUploadModal'

const McpMocksExplorer = () => {
  const [page, setPage] = useState(1)
  // Debounce the search so we hit the backend once the user pauses typing; a
  // new term resets to the first page.
  const {
    state: search,
    debouncedState: debouncedSearch,
    setState: setSearch,
  } = useDebouncedState('', 300, () => setPage(1))
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editing, setEditing] = useState<McpMock | null>(null)

  const filters = useMemo(
    () => ({ q: debouncedSearch || undefined, page, pageSize: MCP_MOCKS_PAGE_SIZE }),
    [debouncedSearch, page],
  )
  const { data, isLoading } = useMcpMocksListQuery(filters)
  const deleteMutation = useDeleteMcpMockMutation()

  const mocks = data?.scenarios ?? []
  const total = data?.total ?? 0

  const renderItem = (mock: McpMock) => (
    <List.Item
      actions={[
        <Tooltip key="delete" title="Delete configuration">
          <Popconfirm
            title="Delete this MCP mock configuration?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(mock.scenario_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>,
        <Tooltip key="edit" title="Edit configuration">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setEditing(mock)}
          />
        </Tooltip>,
        <Tooltip key="download" title="Download JSON">
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => downloadMockJson(mock)}
          />
        </Tooltip>,
      ]}
    >
      <Typography.Text strong>{mock.name}</Typography.Text>
      {mock.used_by_cases > 0 && (
        <Tag style={{ marginLeft: 8 }}>{mock.used_by_cases} case(s)</Tag>
      )}
    </List.Item>
  )

  return (
    <VSpace>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>
        Search Mock Configurations
      </Typography.Title>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input.Search
          placeholder="Search by name"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <Tooltip title="Add configuration">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setUploadOpen(true)}
          />
        </Tooltip>
      </div>
      <List
        bordered
        loading={isLoading}
        dataSource={mocks}
        locale={{ emptyText: 'No mock configurations' }}
        renderItem={renderItem}
      />
      {total > 0 && (
        <Pagination
          current={page}
          pageSize={MCP_MOCKS_PAGE_SIZE}
          total={total}
          onChange={setPage}
          showSizeChanger={false}
          hideOnSinglePage
          showTotal={(t) => `${t} configuration(s)`}
          style={{ textAlign: 'right' }}
        />
      )}
      {uploadOpen && (
        <McpMockUploadModal open onClose={() => setUploadOpen(false)} />
      )}
      {editing && (
        <McpMockUploadModal
          open
          mock={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </VSpace>
  )
}

export default McpMocksExplorer
